// Cách hiển thị vật phẩm dùng chung (tên, màu phẩm, hệ, icon, giá trị dòng). Không tính game logic.
import { t } from '@i18n/t';
import { formatFull } from '@i18n/format';
import { baseDef } from '@data/items/bases';
import { maybeAffix } from '@data/items/affixes';
import { goodsDef, parseStackKey } from '@data/items/goods';
import { RARITIES, type ItemInstance, type ItemLine, type Rarity, type SlotKind } from '@data/items/types';
import type { Element } from '@data/elements';
import { lineValue } from '@sim/items/stats';
import { QUALITIES } from './tokens';
import type { IconName } from './icons';

const EL_CLASS: Record<Element, string> = {
  metal: 'kim',
  wood: 'moc',
  water: 'thuy',
  fire: 'hoa',
  earth: 'tho',
  light: 'quang',
  dark: 'am',
};

export function rarityClass(r: Rarity | number): string {
  const i = typeof r === 'number' ? r : RARITIES.indexOf(r);
  return `q-${QUALITIES[Math.max(0, Math.min(QUALITIES.length - 1, i))]!.id}`;
}

export function elementClass(el: Element | undefined): string | undefined {
  return el ? `el-${EL_CLASS[el]}` : undefined;
}

const SLOT_ICON: Record<SlotKind, IconName> = {
  weapon: 'sword',
  helm: 'helm',
  robe: 'robe',
  pants: 'pants',
  gloves: 'gloves',
  boots: 'boots',
  necklace: 'necklace',
  ring: 'ring',
  treasure: 'treasure',
};

export function slotIcon(kind: SlotKind): IconName {
  return SLOT_ICON[kind];
}

export function itemIcon(it: ItemInstance): IconName {
  const b = baseDef(it.base);
  return b.weaponType === 'saber' ? 'saber' : SLOT_ICON[b.slot];
}

export function artUrl(icon: string | undefined): string | null {
  return icon ? `${import.meta.env.BASE_URL}assets/items/${icon}.webp` : null;
}

export function itemArt(it: ItemInstance): string | null {
  return artUrl(baseDef(it.base).icon);
}

export function itemName(it: ItemInstance): string {
  return t(it.uniqueId ? `${it.uniqueId}.name` : `${it.base}.name`);
}

export function affixName(id: string): string {
  return t(`affix.${id}`);
}

/** "+7,8%" · "+52ms" · "+112" theo đơn vị dòng. */
export function formatAffix(id: string, value: number, giai: number): string {
  const d = maybeAffix(id);
  const v = lineValue(id, value, giai);
  const unit = d?.unit ?? 'pct';
  if (unit === 'pct' || id === 'resAll') return `+${formatFull(Math.round(v * 10) / 10)}%`;
  if (unit === 'ms') return `+${formatFull(Math.round(v))}ms`;
  return `+${formatFull(Math.round(v * 10) / 10)}`;
}

export function lineText(l: ItemLine, giai: number): string {
  return `${affixName(l.affix)} ${formatAffix(l.affix, l.value, giai)}`;
}

// ── Vật phẩm xếp chồng
const GOODS_ICON: Record<string, IconName> = {
  herb: 'herb',
  seed: 'seed',
  pill: 'pill',
  material: 'material',
};

export function goodsIcon(key: string): IconName {
  return GOODS_ICON[goodsDef(parseStackKey(key).id).kind] ?? 'material';
}

export function goodsArt(key: string): string | null {
  return artUrl(goodsDef(parseStackKey(key).id).icon);
}

/** Tên chồng: "Hoàng Tinh · bách niên", "Tụ Khí Đan · trung phẩm". */
export function goodsName(key: string): string {
  const { id, age, grade } = parseStackKey(key);
  const kind = goodsDef(id).kind;
  const base = t(`${id}.name`);
  if (kind === 'pill') return `${base} · ${t(`craft.pillGrade.${grade}`)}`;
  if (kind === 'herb' && age !== 'decade') return `${base} · ${t(`craft.herbAge.${age}`)}`;
  return base;
}

export function goodsTier(key: string): number {
  const { id, grade } = parseStackKey(key);
  const d = goodsDef(id);
  if (d.kind === 'pill') return Math.min(5, d.tier + ['low', 'mid', 'high', 'peak'].indexOf(grade) - 1);
  return d.tier;
}
